/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { Drawer } from '@biq/apex-ui';
import { useSystemMessageDetails } from 'hooks';
import { styles } from './details-drawer-helper';
import { Grid, Typography } from '@mui/material';
import { EmptyStateDrawerDetails, HelixLoader } from 'components';
import { getChipComponent, SystemMessageData } from '../system-messages-helper';
import { commonShowAlert, toLocalDateTimeString } from 'lib';
import { useAlert } from '../../../core';
import ReadOnlyWrapper from 'components/read-only-wrapper/ReadOnlyWrapper';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import utf8 from 'utf8';

interface DetailsDrawerProps {
    drawerOpen: boolean;
    onDrawerClose: () => void;
    selectedMessageId: string;
}

const anchorTag = ({ children, ...props }: any): React.JSX.Element => {
    return (
        <a
            {...props}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={typeof children === 'string' ? children : undefined}
        >
            {children}
        </a>
    );
};

export const DetailsDrawer = ({
    drawerOpen,
    onDrawerClose,
    selectedMessageId,
}: DetailsDrawerProps): React.JSX.Element => {
    const {
        data: messageData,
        isLoading,
        error,
    } = useSystemMessageDetails({
        systemMessageId: selectedMessageId,
    });
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const [drawerData, setDrawerData] = useState<SystemMessageData>({
        contentBase64: '',
        createDate: '',
        messageId: '',
        messageType: 'ALERT',
        title: '',
    });

    useEffect(() => {
        if (!isLoading && messageData?.data) {
            setDrawerData({
                contentBase64: messageData.data.contentBase64,
                createDate: messageData.data.createDate,
                messageId: messageData.data.messageId,
                messageType: messageData.data.messageType,
                title: messageData.data.title,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    useEffect(() => {
        if (error) {
            onDrawerClose();
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'Message details cannot be fetched',
                JSON.stringify(error),
                showErrorDetails
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    if (isLoading) {
        return <HelixLoader />;
    }

    const titleElement: React.JSX.Element = (
        <Grid sx={styles.headerContainer}>
            <Grid sx={styles.headerTextAndChipContainer}>
                <Typography sx={styles.headerTextStyles}>
                    {drawerData.title}
                </Typography>
                <Grid sx={styles.chipContainer}>
                    {getChipComponent(drawerData.messageType)}
                </Grid>
            </Grid>
        </Grid>
    );

    const subheader: React.JSX.Element = (
        <Grid mt={1}>
            <Typography>
                {toLocalDateTimeString(drawerData.createDate, true)}
            </Typography>
        </Grid>
    );

    return (
        <Drawer
            closable
            open={drawerOpen}
            titleChildren={titleElement}
            title={drawerData.title}
            drawerType={'rounded'}
            anchor={'right'}
            sx={styles.detailsDrawer}
            onClose={onDrawerClose}
            subheaderChildren={subheader}
        >
            {drawerData.contentBase64 ? (
                <Grid sx={styles.readOnlyContainer}>
                    <ReadOnlyWrapper>
                        <Grid sx={styles.readOnlyCodeStyles}>
                            <Markdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: (props) => anchorTag(props),
                                }}
                            >
                                {utf8.decode(atob(drawerData.contentBase64))}
                            </Markdown>
                        </Grid>
                    </ReadOnlyWrapper>
                </Grid>
            ) : (
                <EmptyStateDrawerDetails />
            )}
        </Drawer>
    );
};
